/**
 * Horário da loja (Prompt 08): semana, datas especiais e o que fazer com a
 * loja fechada. Calculado no fuso da empresa, sem biblioteca de datas.
 */
import { z } from "zod";
import { horarioTrabalhoSchema, type HorarioTrabalho } from "./equipe";

const data = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use AAAA-MM-DD.");
const hora = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use o formato HH:MM.");

export const MENSAGEM_AUSENCIA_PADRAO =
  "Oi, {nome}! A loja está fechada agora, mas sua mensagem já chegou. {abertura}, uma vendedora continua com você por aqui.";

export const configHorarioLojaSchema = z.object({
  /** Desligado = loja sempre aberta (nada muda no atendimento). */
  ativo: z.boolean().default(false),
  semana: horarioTrabalhoSchema.default({
    "1": [["09:00", "18:00"]],
    "2": [["09:00", "18:00"]],
    "3": [["09:00", "18:00"]],
    "4": [["09:00", "18:00"]],
    "5": [["09:00", "18:00"]],
    "6": [["09:00", "13:00"]],
  }),
  /** Feriados e datas com horário diferente. Sem faixas = fechada o dia todo. */
  especiais: z
    .array(
      z.object({
        data,
        nome: z.string().trim().max(60).default(""),
        faixas: z
          .array(z.tuple([hora, hora]))
          .max(4)
          .default([]),
      }),
    )
    .max(100)
    .default([]),
  /** A assistente virtual recebe a cliente com a loja fechada (precisa da IA). */
  modoAssistido: z.boolean().default(true),
  /** Mensagem de ausência (sem IA, ou se a IA falhar). */
  enviarAusencia: z.boolean().default(true),
  mensagemAusencia: z.string().trim().min(5).max(600).default(MENSAGEM_AUSENCIA_PADRAO),
  /** Não repete a ausência para a mesma cliente antes deste intervalo. */
  intervaloAusenciaHoras: z.number().int().min(1).max(72).default(12),
});
export type ConfigHorarioLoja = z.infer<typeof configHorarioLojaSchema>;
export const CONFIG_HORARIO_LOJA_PADRAO: ConfigHorarioLoja = configHorarioLojaSchema.parse({});

const DIAS_CURTOS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Data, dia da semana e minutos do dia no fuso. */
function partes(d: Date, fuso: string) {
  const p = new Intl.DateTimeFormat("en-US", {
    timeZone: fuso,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  const v = (t: string) => p.find((x) => x.type === t)?.value ?? "";
  return {
    data: `${v("year")}-${v("month")}-${v("day")}`,
    dia: DIAS_CURTOS.indexOf(v("weekday")),
    ano: Number(v("year")),
    mes: Number(v("month")),
    diaMes: Number(v("day")),
    hora: Number(v("hour")),
    minuto: Number(v("minute")),
  };
}

/** "AAAA-MM-DD" + "HH:MM" no fuso → instante UTC. */
function localParaUtc(dataIso: string, hhmm: string, fuso: string): Date {
  const [a, m, d] = dataIso.split("-").map(Number) as [number, number, number];
  const [h, mi] = hhmm.split(":").map(Number) as [number, number];
  const alvo = Date.UTC(a, m - 1, d, h, mi);
  let t = alvo;
  for (let i = 0; i < 2; i++) {
    const p = partes(new Date(t), fuso);
    const comoUtc = Date.UTC(p.ano, p.mes - 1, p.diaMes, p.hora, p.minuto);
    t += alvo - comoUtc;
  }
  return new Date(t);
}

function faixasDoDia(cfg: ConfigHorarioLoja, dataIso: string, dia: number) {
  const especial = cfg.especiais.find((e) => e.data === dataIso);
  if (especial) return especial.faixas;
  return cfg.semana[String(dia) as keyof HorarioTrabalho] ?? [];
}

export interface SituacaoLoja {
  aberta: boolean;
  /** Próximo horário de abertura (null se aberta ou sem horário nos próximos 14 dias). */
  proximaAbertura: Date | null;
}

export function situacaoLoja(
  cfg: ConfigHorarioLoja,
  fuso: string,
  agora = new Date(),
): SituacaoLoja {
  if (!cfg.ativo) return { aberta: true, proximaAbertura: null };
  let proxima: Date | null = null;
  for (let d = 0; d <= 14; d++) {
    const p = partes(new Date(agora.getTime() + d * 86_400_000), fuso);
    for (const [ini, fim] of faixasDoDia(cfg, p.data, p.dia)) {
      const inicio = localParaUtc(p.data, ini, fuso);
      const termino = localParaUtc(p.data, fim, fuso);
      if (d === 0 && agora >= inicio && agora < termino)
        return { aberta: true, proximaAbertura: null };
      if (inicio > agora && (!proxima || inicio < proxima)) proxima = inicio;
    }
    if (proxima) break;
  }
  return { aberta: false, proximaAbertura: proxima };
}

const NOMES_DIA = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

/** "Hoje às 14h", "Amanhã às 9h", "Segunda às 9h30", "Em 25/12 às 10h". */
export function descreverAbertura(abertura: Date | null, fuso: string, agora = new Date()): string {
  if (!abertura) return "Assim que a loja abrir";
  const a = partes(abertura, fuso);
  const hoje = partes(agora, fuso);
  const amanha = partes(new Date(agora.getTime() + 86_400_000), fuso);
  const hora = `${a.hora}h${a.minuto ? String(a.minuto).padStart(2, "0") : ""}`;
  const dias = Math.round((abertura.getTime() - agora.getTime()) / 86_400_000);
  if (a.data === hoje.data) return `Hoje às ${hora}`;
  if (a.data === amanha.data) return `Amanhã às ${hora}`;
  if (dias < 7) {
    const n = NOMES_DIA[a.dia]!;
    return `${n[0]!.toUpperCase()}${n.slice(1)} às ${hora}`;
  }
  return `Em ${String(a.diaMes).padStart(2, "0")}/${String(a.mes).padStart(2, "0")} às ${hora}`;
}

/** Variáveis da mensagem de ausência: {nome}, {abertura}, {loja}. */
export function mensagemDeAusencia(
  modelo: string,
  v: { nome: string | null; abertura: string; loja: string },
) {
  const primeiro = v.nome?.trim().split(/\s+/)[0] ?? "";
  return modelo
    .replaceAll("{nome}", primeiro)
    .replaceAll("{abertura}", v.abertura)
    .replaceAll("{loja}", v.loja)
    .replace(/Oi, !/g, "Oi!")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/** Resposta padrão de um canal só de comunicação (avisos automáticos). */
export const RESPOSTA_CANAL_COMUNICACAO_PADRAO =
  "Oi, {nome}! Este número envia só avisos automáticos e não é acompanhado pela equipe. Para falar com a gente, chame no WhatsApp {principal}.";

export function mensagemCanalComunicacao(
  modelo: string,
  v: { nome: string | null; principal: string | null; loja: string },
) {
  const primeiro = v.nome?.trim().split(/\s+/)[0] ?? "";
  return modelo
    .replaceAll("{nome}", primeiro)
    .replaceAll("{principal}", v.principal ?? "principal da loja")
    .replaceAll("{loja}", v.loja)
    .replace(/Oi, !/g, "Oi!")
    .replace(/\s{2,}/g, " ")
    .trim();
}
